import { Button, DrawerPage, DrawerPanel, Text } from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

import { hasQuickFilterValue } from "#app/lib/filters/quick-values";
import type { UseFiltersResult } from "#app/lib/filters/types";
import { QuickDatePage } from "./quick-inputs/quick-date-page";
import {
  writeQuickValue,
  type QuickFilterSection,
} from "./quick-inputs/quick-filter-options";
import { QuickOptionList } from "./quick-inputs/quick-option-list";
import {
  isEntityPickerSection,
  QuickEntityPicker,
} from "#app/components/filters/quick-inputs/quick-entity-picker";

type QuickFilterPageProps = Omittable<{
  section: QuickFilterSection;
  filters: UseFiltersResult;
}>;

/** Whether a filter's row drills into a page of its own. A flag is its own
 *  switch and a pool of nothing has no list to open; a date window has its
 *  preset ramp even though the index enumerates no member for a custom
 *  range. */
export function isDrillableSection({ def, options }: QuickFilterSection) {
  return (
    def.type === "last-updated" ||
    (def.type !== "more-options" && options.length > 0)
  );
}

/**
 * ONE quick filter's values as a page pushed over the filter list on a phone
 * (`DrawerPage`, keyed by the filter's field), headed by the filter's own name
 * and a Clear for the value it holds, on the same quiet `xs` rung as the
 * root header's Save and Clear all so the two headers read as one family.
 *
 * The Clear is the ONE per-filter clear the panel has, and it lives here
 * because the page is about one filter: with the other rows covered there is
 * no "Clear all" inches away for it to disagree with. The root list stays
 * mounted under the page, so the value set here reads back on its row the
 * moment the page pops.
 */
export function QuickFilterPage({ section, filters }: QuickFilterPageProps) {
  const { def, options } = section;
  const value = filters.quickValues[def.field];
  const setValue = (next: unknown) => writeQuickValue(filters, section, next);

  return (
    <DrawerPage
      value={def.field}
      title={def.label}
      action={
        hasQuickFilterValue(value) ? (
          <Button size="xs" variant="ghost" onClick={() => setValue(null)}>
            <Text size="xs" variant="muted">
              Clear
            </Text>
          </Button>
        ) : null
      }
    >
      {isEntityPickerSection(section) ? (
        <DrawerPanel variant="workspace">
          <QuickEntityPicker section={section} value={value} onChange={setValue} />
        </DrawerPanel>
      ) : def.type === "last-updated" ? (
        <DrawerPanel className="px-0 pt-0">
          <QuickDatePage options={options} value={value} onChange={setValue} />
        </DrawerPanel>
      ) : (
        <DrawerPanel variant="workspace">
          <QuickOptionList
            options={options}
            value={value}
            onChange={setValue}
            label={def.label}
          />
        </DrawerPanel>
      )}
    </DrawerPage>
  );
}
